import { Button, type ButtonProps } from "@mui/material";
import { Home, LogoutOutlined, Refresh } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";

/** `primary` marks the recommended action; the rest are `secondary`. */
type Emphasis = "primary" | "secondary";

const shape = { width: { xs: "100%", sm: "169px" }, borderRadius: "20px" };

// Contrast, both themes (background.brand is the same in each):
// primary: background.brand text on secondary.main 5.61:1 / 5.66:1 (text.primary
// would be white in dark mode, 2.78:1); button on background.brand 5.60:1 / 5.66:1.
// secondary: common.white text and border on background.brand 15.73:1.
const emphasisStyle: Record<Emphasis, Pick<ButtonProps, "variant" | "color" | "sx">> = {
  primary: {
    variant: "contained",
    color: "secondary",
    sx: { ...shape, color: "background.brand" },
  },
  secondary: {
    variant: "outlined",
    sx: { ...shape, color: "common.white", borderColor: "common.white" },
  },
};

export function GoHomeButton({ emphasis = "primary" }: { emphasis?: Emphasis }) {
  return (
    <Button
      component={RouterLink}
      to="/"
      size="large"
      startIcon={<Home />}
      {...emphasisStyle[emphasis]}
    >
      Go to Home
    </Button>
  );
}

export function ReloadButton() {
  return (
    <Button
      size="large"
      startIcon={<Refresh />}
      onClick={() => window.location.reload()}
      {...emphasisStyle.primary}
    >
      Reload
    </Button>
  );
}

/**
 * Runs the caller's recovery, e.g. reloading the app root without Auth0's
 * error parameters. Unlike ReloadButton, which reloads the current URL as is.
 */
export function TryAgainButton({ onClick }: { onClick: () => void }) {
  return (
    <Button size="large" startIcon={<Refresh />} onClick={onClick} {...emphasisStyle.primary}>
      Try again
    </Button>
  );
}

/** Rendered by StatusPage from its `onSignOut` prop. */
export function SignOutButton({
  emphasis,
  onSignOut,
}: {
  emphasis: Emphasis;
  onSignOut: () => void;
}) {
  return (
    <Button
      size="large"
      startIcon={<LogoutOutlined />}
      onClick={onSignOut}
      {...emphasisStyle[emphasis]}
    >
      Sign Out
    </Button>
  );
}
